/**
 * The derived text of the hearings register and case file — §5's Match Ref cell, §6's
 * Outcome column, §8's stepper state machine and §4's Case Load figures.
 *
 * Everything here is a **function of the case record**, never a stored word. That is the same
 * promise `@hockeytd/shared`'s `deriveHearingStage` makes about the stage itself, extended to
 * the sentences the register prints beside it: a register that stored `Decision Outstanding`
 * would say it about a case whose decision had since been issued.
 *
 * **Nothing in this file restates a label, a tooltip or a transition rule.** The stage chips,
 * the batch titles, the document-state chips, the gate messages, the minimum penalties and
 * the legal transitions all come from `@hockeytd/shared/hearings`, which is the single copy
 * the API reads too. What lives here is only the composition §5, §6 and §8 describe and the
 * shared package does not hold.
 *
 * **Time zone.** Instants are rendered in `Asia/Tokyo`, the tournament's own zone, exactly as
 * `UmTodayPage` and `OwnershipPanel` render theirs (spec 00 §7.3). Date-only strings are
 * parsed at UTC midnight and formatted at UTC, so a `YYYY-MM-DD` cannot slip a day.
 *
 * British English, Title Case labels, leading-cap sentence hints — the standing copy rule.
 */

import {
  HEARING_LEVEL_MINIMUM_PENALTY,
  deriveHearingCaseLevel,
  isHearingOpen,
  type HearingLevel,
  type HearingStage,
} from "@hockeytd/shared";

import type {
  HearingCaseView,
  HearingChargeView,
  HearingPersonView,
} from "../../api/types.js";

/** `Tue 22 Sep` from a `YYYY-MM-DD`. Returns the input unchanged if it will not parse. */
export function formatMatchDay(date: string | null): string | null {
  if (date === null || date === "") return null;
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(parsed);
}

/** `Tue 23 Sep` — an instant, in the tournament's own zone. */
export function formatDay(at: string | null): string | null {
  if (at === null || at === "") return null;
  const parsed = new Date(at);
  if (Number.isNaN(parsed.getTime())) return at;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tokyo",
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(parsed);
}

/** `Tue 23 Sep, 20:00` — §14's format for a hearing time, in the tournament's own zone. */
export function formatInstant(at: string | null): string | null {
  if (at === null || at === "") return null;
  const parsed = new Date(at);
  if (Number.isNaN(parsed.getTime())) return at;
  const day = formatDay(at) ?? "";
  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tokyo",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(parsed);
  return `${day}, ${time}`;
}

/**
 * §5's Match Ref cell: `M09 · Tue 22 Sep`, or `No match · {date}` for an off-pitch incident.
 *
 * The match number is taken from `matchRef` exactly as the server sends it and is never
 * composed here — §10 requires the women's competition to display `W`-prefixed and forbids
 * arithmetic on fixture identifiers anywhere in this module. The fallback date on a
 * no-match case is the date the case was created, which is the only date such a case has.
 */
export function matchRefText(hearing: HearingCaseView): string {
  if (hearing.matchRef === null) {
    return `No match · ${formatDay(hearing.createdAt) ?? "—"}`;
  }
  const day = formatMatchDay(hearing.matchDate);
  return day === null ? hearing.matchRef : `${hearing.matchRef} · ${day}`;
}

/**
 * §5's Persons Involved cell — `Player #7`, or the role on its own for somebody who wears no
 * shirt number.
 *
 * There used to be a second spelling of this, `personRoleLine`, which put the role on a line
 * of its own as `Player · No 7`. It has no caller left and is gone: the register and the case
 * file both read the short form, and two spellings of one fact are how they drift apart.
 *
 * The register printed the name and the role as two stacked lines with a 26px gap between
 * them, and the Technical Delegate read that gap as a fault rather than as a layout: *"the
 * gap between the name and the role is far too big"*. The role is not a second line of
 * information about a different thing — it is how the person is identified — so it now sits
 * beside the name on one line, separated by a middle dot, and wraps as ordinary words do.
 *
 * `#` rather than `No` because the shirt number is being read off a list at a glance here,
 * not written into a letter; §10's letter still spells `PLAYER NO 7` in full, from
 * `@hockeytd/shared`, and nothing in this file touches that.
 */
export function personRoleShort(person: HearingPersonView): string {
  return person.shirtNumber === null
    ? person.role
    : `${person.role} #${String(person.shirtNumber)}`;
}

/** §7's level-chip tooltip, rendered `title="Minimum penalty: …"`. */
export function minimumPenaltyTitle(level: HearingLevel): string {
  return `Minimum penalty: ${HEARING_LEVEL_MINIMUM_PENALTY[level]}`;
}

/** §7: the case level is the maximum across its charges, derived at render time. */
export function caseLevel(charges: readonly HearingChargeView[]): HearingLevel | null {
  return deriveHearingCaseLevel(charges.map((charge) => charge.level));
}

/**
 * §6's Outcome column.
 *
 * Four of the five stages compose from the case record and do. The fifth does not, and this
 * is the one place on the register where that shows: §6 wants `decision` to print **the
 * outcome**, plus ` · Referred Upward` where a referral exists — and the decision record and
 * the referral both live in tables nothing writes yet (`repositories/hearings.ts`'s header).
 * So a decided case says that its decision letter has been issued and that the outcome is not
 * recorded, which is true, rather than an invented sanction, which would be printed.
 */
export interface OutcomeText {
  text: string;
  /** A `title` explaining an honest gap, where there is one. */
  title?: string;
}

export function outcomeText(hearing: HearingCaseView): OutcomeText {
  switch (hearing.stage) {
    case "closed":
      return { text: "Closed — Informal Enquiry" };
    case "decision":
      return {
        text: "Decision Issued · Outcome Not Recorded",
        title:
          "The decision record and the referral flag are not built yet, so the outcome and any referral upward cannot be shown.",
      };
    case "minutes":
      return { text: "Decision Outstanding" };
    case "letter": {
      const at = formatInstant(hearing.hearingAt);
      return { text: at === null ? "Hearing Scheduled" : `Hearing ${at}` };
    }
    case "created":
    default:
      return { text: "Letter Pending" };
  }
}

/* ── §8's stepper ─────────────────────────────────────────────────────────── */

export type StepState = "done" | "current" | "todo" | "skipped";

/** §8's five step titles, in order. */
export const HEARING_STEP_TITLES: readonly string[] = [
  "Case Created",
  "Hearing Letter",
  "Hearing Held",
  "Decision Issued",
  "Penalty Tracked",
];

/** §8's per-step descriptors, in the same order. */
export const HEARING_STEP_DESCRIPTORS: readonly string[] = [
  "Incident, persons involved, charges",
  "Fact of report, who must attend, time and place, conduct and proof",
  "Chaired by the TD, assistant takes the minutes",
  "Accepts or not, finding, penalty",
  "Matches, commencement, exact matches, referral",
];

/**
 * §8's step state machine, verbatim from the spec's table and stated nowhere else.
 *
 * It is keyed on the stage rather than read off the three documents a second time, because
 * the stage is already the one derivation of those documents (`deriveHearingStage`) and a
 * second reading could disagree with the chip printed beside it.
 */
const STEP_STATES: Readonly<Record<HearingStage, readonly StepState[]>> = {
  closed: ["done", "skipped", "skipped", "skipped", "skipped"],
  created: ["done", "current", "todo", "todo", "todo"],
  letter: ["done", "done", "current", "todo", "todo"],
  minutes: ["done", "done", "done", "current", "todo"],
  decision: ["done", "done", "done", "done", "done"],
};

export function stepStates(stage: HearingStage): readonly StepState[] {
  return STEP_STATES[stage];
}

/* ── §4's Case Load ───────────────────────────────────────────────────────── */

export interface CaseLoadFigure {
  key: string;
  /** The figure, or `null` where the underlying record is not built. */
  value: number | null;
  sub: string;
  /** `fig--good` / `fig--alert` in §4's table, as a tone this build can carry. */
  tone: "plain" | "good" | "alert" | "unbuilt";
}

/**
 * §4's five figures, every one computed from the register below it — which is what the
 * panel's own note promises: `Every Figure Computed From The Case Register Below`.
 *
 * **The fifth is not computed, because it cannot be.** `Suspensions Active` counts cases with
 * `penalty.matches > 0`, and no penalty is recorded anywhere in this slice — the suspension
 * tables of §13 have no writer. It is returned with a null value and an `unbuilt` tone so the
 * strip prints the gap rather than a confident `0`, which a TD would read as "nobody is
 * suspended" instead of "this is not tracked yet".
 */
export function caseLoadFigures(hearings: readonly HearingCaseView[]): CaseLoadFigure[] {
  const total = hearings.length;
  const open = hearings.filter((hearing) => isHearingOpen(hearing.stage)).length;
  const awaiting = hearings.filter((hearing) => hearing.stage === "letter").length;
  const decided = hearings.filter((hearing) => hearing.stage === "decision").length;

  return [
    {
      key: "Total Cases",
      value: total,
      sub: `${String(total)} ${total === 1 ? "case" : "cases"} opened this tournament`,
      tone: "plain",
    },
    {
      key: "Open",
      value: open,
      sub: "Letter, hearing or decision outstanding",
      tone: "plain",
    },
    {
      key: "Awaiting Hearing",
      value: awaiting,
      sub: "Letter issued, hearing not yet held",
      tone: "plain",
    },
    {
      key: "Decided",
      value: decided,
      sub: "Decision letter issued and served",
      tone: "good",
    },
    {
      key: "Suspensions Active",
      value: null,
      sub: "Not built — no penalty or referral is recorded yet",
      tone: "unbuilt",
    },
  ];
}

/**
 * §8's panel note: `DC-05 · KOR · PARK Jae-won · Match M08, Tue 22 Sep`, or the same line
 * ending `· No match reference`.
 */
export function caseFileNote(hearing: HearingCaseView): string {
  const parts = caseFileNoteParts(hearing);
  return [parts.caseNumber, ...(parts.teamCode === null ? [] : [parts.teamCode]), parts.rest].join(
    " · ",
  );
}

/**
 * The same note, in the three pieces the panel draws it in — because the team code now
 * carries a flag beside it, and a flag cannot live inside a string.
 *
 * `caseFileNote` composes these into the one line the specification writes, and the two can
 * therefore never say different things about the same case.
 */
export interface CaseFileNoteParts {
  caseNumber: string;
  /** Null where the case names no team; the panel then draws no flag rather than a guess. */
  teamCode: string | null;
  /** The persons involved and the match reference, joined as §8 writes them. */
  rest: string;
}

export function caseFileNoteParts(hearing: HearingCaseView): CaseFileNoteParts {
  const rest: string[] = hearing.personsInvolved.map((person) => person.name);
  if (hearing.matchRef === null) {
    rest.push("No match reference");
  } else {
    const day = formatMatchDay(hearing.matchDate);
    rest.push(day === null ? `Match ${hearing.matchRef}` : `Match ${hearing.matchRef}, ${day}`);
  }
  return {
    caseNumber: hearing.caseNumber,
    teamCode: hearing.teamCode,
    rest: rest.join(" · "),
  };
}
